/**
 * Navigation.
 *
 * Every entry maps to a real backend capability. Modules from the original brief
 * that have no API behind them (calendar, customers, teams, notifications,
 * integrations, export centre) are deliberately absent — see ADMIN_WEB_UX.md §1.
 * An empty nav item makes the product look broken and teaches users to ignore the menu.
 */

import {
  BarChart3,
  Building2,
  ClipboardList,
  FileStack,
  Gauge,
  History,
  Home,
  KeyRound,
  Layers,
  Palette,
  Ruler,
  ScrollText,
  Settings,
  ShieldCheck,
  Tags,
  TriangleAlert,
  Users,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { PERMISSIONS, type Permission } from '@/lib/permissions'

export interface NavItem {
  label: string
  to: string
  icon: LucideIcon
  permission?: Permission
  /** Match child routes for the active state, e.g. /inspections/:id. */
  matchPrefix?: boolean
}

export interface NavGroup {
  label: string | null
  items: NavItem[]
}

export const NAVIGATION: NavGroup[] = [
  {
    label: null,
    items: [{ label: 'Dashboard', to: '/dashboard', icon: Home }],
  },
  {
    label: 'Operations',
    items: [
      {
        label: 'Inspections',
        to: '/inspections',
        icon: ClipboardList,
        permission: PERMISSIONS.INSPECTION_VIEW,
        matchPrefix: true,
      },
      {
        label: 'Snag board',
        to: '/issues',
        icon: TriangleAlert,
        permission: PERMISSIONS.ISSUE_VIEW,
      },
      {
        label: 'Access codes',
        to: '/access-codes',
        icon: KeyRound,
        permission: PERMISSIONS.ACCESS_CODE_VIEW,
      },
    ],
  },
  {
    label: 'Properties',
    items: [
      {
        label: 'Projects',
        to: '/properties/projects',
        icon: Building2,
        permission: PERMISSIONS.PROPERTY_VIEW,
        matchPrefix: true,
      },
      {
        label: 'Units',
        to: '/properties/units',
        icon: Gauge,
        permission: PERMISSIONS.PROPERTY_VIEW,
        matchPrefix: true,
      },
    ],
  },
  {
    label: 'Configuration',
    items: [
      {
        label: 'Templates',
        to: '/templates',
        icon: FileStack,
        permission: PERMISSIONS.TEMPLATE_VIEW,
        matchPrefix: true,
      },
      {
        label: 'Template mapping',
        to: '/templates/mapping',
        icon: Layers,
        permission: PERMISSIONS.TEMPLATE_VIEW,
      },
      {
        label: 'Measurements',
        to: '/measurements',
        icon: Ruler,
        permission: PERMISSIONS.MEASUREMENT_SCOPE_VIEW,
      },
      {
        label: 'Taxonomies',
        to: '/settings/taxonomies',
        icon: Tags,
        permission: PERMISSIONS.SETTINGS_MANAGE,
      },
    ],
  },
  {
    label: 'Analytics',
    items: [
      {
        label: 'Reports',
        to: '/reports',
        icon: BarChart3,
        permission: PERMISSIONS.REPORT_VIEW,
      },
    ],
  },
  {
    label: 'People',
    items: [
      {
        label: 'Members',
        to: '/people/members',
        icon: Users,
        permission: PERMISSIONS.MEMBER_VIEW,
        matchPrefix: true,
      },
      {
        label: 'Inspectors',
        to: '/people/inspectors',
        icon: ShieldCheck,
        permission: PERMISSIONS.MEMBER_VIEW,
      },
      {
        label: 'Roles',
        to: '/settings/roles',
        icon: ScrollText,
        permission: PERMISSIONS.MEMBER_VIEW,
      },
    ],
  },
  {
    label: 'Organisation',
    items: [
      {
        label: 'Settings',
        to: '/settings/organisation',
        icon: Settings,
        permission: PERMISSIONS.SETTINGS_MANAGE,
      },
      {
        label: 'Branding',
        to: '/settings/branding',
        icon: Palette,
        permission: PERMISSIONS.SETTINGS_MANAGE,
      },
      {
        label: 'Audit log',
        to: '/audit',
        icon: History,
        permission: PERMISSIONS.AUDIT_VIEW,
      },
    ],
  },
]

/** Drop groups the user cannot use at all, so nobody sees an empty heading. */
export function visibleNavigation(permissions: readonly string[]): NavGroup[] {
  return NAVIGATION.map((group) => ({
    ...group,
    items: group.items.filter(
      (item) => !item.permission || permissions.includes(item.permission),
    ),
  })).filter((group) => group.items.length > 0)
}
